import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';

/**
 * A part's shelf locations, main one first (migration 144), or "location unknown".
 *
 * `canEmpty` adds the "shelf empty" button next to each location — the picker (counter or
 * warehouse) has just taken the last part from that shelf, so nobody makes the detour afterwards.
 * It only appears while at least one OTHER location remains: a product's last location is never
 * removed (that is "Replace" on the shelving screen), and the server refuses it anyway.
 */
export function ShelfLocations({
  productId,
  locations,
  canEmpty = false,
  onChange,
  className,
}: {
  productId: string;
  locations: string[];
  canEmpty?: boolean;
  onChange?: (locations: string[]) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  if (locations.length === 0) {
    return (
      <Badge variant="muted" className={className}>
        {t('shelving.loc.unknown')}
      </Badge>
    );
  }

  const empty = async (location: string): Promise<void> => {
    setBusy(location);
    try {
      const { data } = await apiClient.post<{ locations: string[] }>(
        '/warehouse/locations/shelf-empty',
        { productId, location }
      );
      push({ tone: 'success', message: t('shelving.loc.removed', { location }) });
      onChange?.(data.locations);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className ?? ''}`}>
      {locations.map((loc, i) => (
        <span key={loc} className="inline-flex items-center gap-1">
          <Badge variant={i === 0 ? 'secondary' : 'outline'} className="font-mono">
            {loc}
            {i === 0 && locations.length > 1 && (
              <span className="ml-1 font-sans text-[10px] uppercase opacity-70">
                {t('shelving.loc.main')}
              </span>
            )}
          </Badge>
          {canEmpty && locations.length > 1 && (
            <button
              type="button"
              className="rounded border px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted disabled:opacity-50"
              title={t('shelving.loc.shelfEmptyHint', { location: loc })}
              disabled={busy !== null}
              onClick={() => void empty(loc)}
            >
              {t('shelving.loc.shelfEmpty')}
            </button>
          )}
        </span>
      ))}
    </span>
  );
}
